/* Tracker Card - interactive instructions + variant selector */
(function () {
  'use strict';

  var reducedMotion = window.matchMedia('(prefers-reduced-motion: reduce)');

  function wait(ms) {
    return new Promise(function (resolve) {
      setTimeout(resolve, ms);
    });
  }

  /* ---- Interactive instructions widget ---- */
  if (!customElements.get('tracker-instructions')) {
    customElements.define(
      'tracker-instructions',
      class TrackerInstructions extends HTMLElement {
        connectedCallback() {
          this.animToken = 0;
          this.tabs = Array.prototype.slice.call(this.querySelectorAll('[data-tracker-tab]'));
          this.panels = Array.prototype.slice.call(this.querySelectorAll('[data-tracker-panel]'));
          this.hotspots = Array.prototype.slice.call(this.querySelectorAll('[data-tracker-hotspot]'));
          this.card = this.querySelector('[data-tracker-card]');
          this.beeps = Array.prototype.slice.call(this.querySelectorAll('[data-tracker-beeps] span'));
          this.beepsLabel = this.querySelector('[data-tracker-beeps-label]');
          if (!this.card || !this.tabs.length) return;

          this.tabs.forEach(
            function (tab, index) {
              tab.addEventListener('click', this.setMode.bind(this, index, true));
              tab.addEventListener('keydown', this.onTabKeydown.bind(this, index));
            }.bind(this)
          );

          this.hotspots.forEach(
            function (hotspot) {
              hotspot.addEventListener('click', this.onHotspotClick.bind(this, hotspot));
            }.bind(this)
          );

          this.panels.forEach(
            function (panel, panelIndex) {
              var steps = Array.prototype.slice.call(panel.querySelectorAll('[data-tracker-step]'));
              steps.forEach(
                function (step, stepIndex) {
                  step.addEventListener('click', this.playStep.bind(this, panelIndex, stepIndex, true));
                }.bind(this)
              );
            }.bind(this)
          );

          this.setMode(0, false);
        }

        disconnectedCallback() {
          this.animToken++;
        }

        onTabKeydown(index, event) {
          var next = null;
          if (event.key === 'ArrowRight' || event.key === 'ArrowDown') next = (index + 1) % this.tabs.length;
          if (event.key === 'ArrowLeft' || event.key === 'ArrowUp') next = (index - 1 + this.tabs.length) % this.tabs.length;
          if (event.key === 'Home') next = 0;
          if (event.key === 'End') next = this.tabs.length - 1;
          if (next === null) return;
          event.preventDefault();
          this.tabs[next].focus();
          this.setMode(next, true);
        }

        onHotspotClick(hotspot) {
          var part = hotspot.getAttribute('data-tracker-hotspot');
          var index = this.tabs.findIndex(function (tab) {
            return tab.getAttribute('data-part') === part;
          });
          if (index >= 0) this.setMode(index, true);
        }

        setMode(index, animate) {
          this.activeMode = index;
          this.activeStep = 0;
          var part = this.tabs[index].getAttribute('data-part');

          this.tabs.forEach(function (tab, i) {
            var selected = i === index;
            tab.setAttribute('aria-selected', selected ? 'true' : 'false');
            tab.setAttribute('tabindex', selected ? '0' : '-1');
          });
          this.panels.forEach(function (panel, i) {
            panel.hidden = i !== index;
          });
          this.hotspots.forEach(function (hotspot) {
            hotspot.classList.toggle('is-lit', hotspot.getAttribute('data-tracker-hotspot') === part);
          });

          this.playStep(index, 0, animate);
        }

        playStep(panelIndex, stepIndex, animate) {
          if (panelIndex !== this.activeMode) return;
          this.activeStep = stepIndex;
          var panel = this.panels[panelIndex];
          var steps = Array.prototype.slice.call(panel.querySelectorAll('[data-tracker-step]'));
          steps.forEach(function (step, i) {
            step.classList.toggle('is-active', i === stepIndex);
          });

          var step = steps[stepIndex];
          var token = ++this.animToken;
          this.resetCard();
          if (!step) return;

          var anim = step.getAttribute('data-anim') || 'none';
          var presses = parseInt(step.getAttribute('data-presses'), 10) || 0;
          var label = step.getAttribute('data-anim-label') || '';

          if (this.beepsLabel) this.beepsLabel.textContent = anim === 'none' ? '' : label;
          if (anim === 'none') return;

          if (anim === 'led') {
            this.card.classList.add('is-led-on');
            return;
          }
          if (anim === 'ring') {
            if (animate && !reducedMotion.matches) {
              this.card.classList.remove('is-ringing');
              void this.card.offsetWidth;
              this.card.classList.add('is-ringing');
            }
            return;
          }
          if (!animate && !presses) return;
          this.runPresses(token, presses, anim === 'press_hold');
        }

        runPresses(token, presses, hold) {
          var self = this;
          if (reducedMotion.matches) {
            this.beeps.forEach(function (beep, i) {
              beep.classList.toggle('is-on', i < presses);
            });
            if (hold) this.card.classList.add('is-pressing');
            return;
          }
          var run = function (p) {
            if (token !== self.animToken) return;
            if (p >= presses) {
              if (hold) self.card.classList.add('is-pressing');
              return;
            }
            self.card.classList.remove('is-pressing');
            void self.card.offsetWidth;
            self.card.classList.add('is-pressing');
            if (self.beeps[p]) self.beeps[p].classList.add('is-on');
            wait(hold ? 700 : 480).then(function () {
              run(p + 1);
            });
          };
          run(0);
        }

        resetCard() {
          this.card.classList.remove('is-led-on', 'is-pressing', 'is-ringing');
          this.beeps.forEach(function (beep) {
            beep.classList.remove('is-on');
          });
          if (this.beepsLabel) this.beepsLabel.textContent = '';
        }
      }
    );
  }

  /* ---- Variant selector (platform + card version) ---- */
  if (!customElements.get('tracker-selector')) {
    var STORAGE_KEY = 'elysian:tracker-variant';

    customElements.define(
      'tracker-selector',
      class TrackerSelector extends HTMLElement {
        connectedCallback() {
          this.designMode = this.getAttribute('data-design-mode') === 'true';
          this.platformButtons = Array.prototype.slice.call(this.querySelectorAll('[data-tracker-platform]'));
          this.versionButtons = Array.prototype.slice.call(this.querySelectorAll('[data-tracker-version]'));
          this.versionGroup = this.querySelector('[data-tracker-version-group]');
          this.unavailable = this.querySelector('[data-tracker-unavailable]');
          this.platform = null;
          this.version = null;

          this.platformButtons.forEach(
            function (button) {
              button.addEventListener('click', this.onChoice.bind(this, 'platform', button.getAttribute('data-tracker-platform')));
            }.bind(this)
          );
          this.versionButtons.forEach(
            function (button) {
              button.addEventListener('click', this.onChoice.bind(this, 'version', button.getAttribute('data-tracker-version')));
            }.bind(this)
          );

          if (!this.designMode) {
            this.hideAllVariants();
            this.restoreSelection();
          }
          this.render(false);
        }

        variantHolders() {
          return Array.prototype.slice.call(document.querySelectorAll('[data-tracker-variant]')).map(function (element) {
            return element.closest('.shopify-section') || element;
          });
        }

        hideAllVariants() {
          this.variantHolders().forEach(function (holder) {
            holder.classList.add('tracker-instructions--waiting');
          });
        }

        restoreSelection() {
          var stored = null;
          try {
            var params = new URLSearchParams(window.location.search);
            stored = params.get('tracker') || window.localStorage.getItem(STORAGE_KEY);
          } catch (error) {
            stored = null;
          }
          if (!stored) stored = this.getAttribute('data-default-variant');
          if (!stored) return;
          var parts = stored.split('-');
          if (parts.length !== 2) return;
          this.platform = parts[0];
          this.version = parts[1];
          this.applyVariant(false);
        }

        onChoice(kind, value) {
          if (kind === 'platform') this.platform = value;
          if (kind === 'version') this.version = value;
          this.render(kind);
        }

        render(trigger) {
          this.platformButtons.forEach(
            function (button) {
              button.setAttribute('aria-pressed', button.getAttribute('data-tracker-platform') === this.platform ? 'true' : 'false');
            }.bind(this)
          );
          this.versionButtons.forEach(
            function (button) {
              button.setAttribute('aria-pressed', button.getAttribute('data-tracker-version') === this.version ? 'true' : 'false');
            }.bind(this)
          );
          if (this.versionGroup) this.versionGroup.hidden = !this.platform;

          if (this.platform && this.version) {
            this.applyVariant(trigger === 'version');
          }
        }

        applyVariant(shouldScroll) {
          var key = this.platform + '-' + this.version;
          if (this.designMode) return;

          try {
            window.localStorage.setItem(STORAGE_KEY, key);
            var url = new URL(window.location.href);
            url.searchParams.set('tracker', key);
            window.history.replaceState(null, '', url.toString());
          } catch (error) {
            /* private mode: selection simply won't persist */
          }

          var match = null;
          this.variantHolders().forEach(function (holder) {
            var element = holder.matches('[data-tracker-variant]') ? holder : holder.querySelector('[data-tracker-variant]');
            var isMatch = element && element.getAttribute('data-tracker-variant') === key;
            holder.classList.toggle('tracker-instructions--waiting', !isMatch);
            if (isMatch) match = holder;
          });

          if (this.unavailable) this.unavailable.hidden = !!match;

          if (match && shouldScroll) {
            match.scrollIntoView({
              behavior: reducedMotion.matches ? 'auto' : 'smooth',
              block: 'start',
            });
          }
        }
      }
    );
  }
})();
